import DownloadIcon from "assets/icons/Download";
import ExclamatoryIcon from "assets/icons/ExclamatoryIcon";
import classNames from "classnames";
import {Button, Loader} from "components";
import {useAppSelector} from "hooks";
import {TUrlImportItem} from "mapx-components/BulkImport/types";
import BulkImportReportProgressBar from "mapx-components/Modals/BulkImportModal/Report/progressBar";
import BulkImportReportToolTip from "mapx-components/Modals/BulkImportModal/Report/Tooltip";
import {FC, useMemo} from "react";
import {BiErrorCircle, BiRefresh} from "react-icons/bi";
import {projectSelector} from "store/project-list/projectListSelectors";
import styles from "./report.module.scss";
import {TBulkImportReportProps} from "./types";
import {
	CalculateStatusTypeCounts,
	formatETA,
	isInProgressStatus,
	STATUS_COLOR_MAP,
	STATUS_MESSAGES,
} from "./utils";

const BulkImportReport: FC<TBulkImportReportProps> = ({
	linkedInUrl,
	handleClick,
	handleDownload,
	inProgress,
	lastCompletedDisplay,
	importedLinkedInUrls,
	totalURLs,
	source,
	isLastBulkImportDataLoading,
	importPercentage,
	loadingImportHistoryDownload,
	labels,
}) => {
	const project = useAppSelector(projectSelector);

	const counts = useMemo(() => {
		return CalculateStatusTypeCounts(linkedInUrl);
	}, [linkedInUrl]);

	// When lastCompletedDisplay is true, the background task is already done.
	// importPercentage is irrelevant and should not block the UI.
	const isEffectivelyComplete = lastCompletedDisplay || importPercentage === 100;

	// Compute progress display: "completed imports / total imports"
	const progressCountDisplayString = useMemo(() => {
		const total = linkedInUrl?.length || 0;
		const nonProgressing = linkedInUrl?.filter(
			({status}) =>
				!["waiting", "importing", "scraping", "in progress", "created", "scraped"].includes(
					status?.toLowerCase(),
				),
		).length;

		return `${nonProgressing}/${total}`;
	}, [linkedInUrl]);

	const getStatusColor = (status: string) => STATUS_COLOR_MAP[status.toLowerCase()] || "#a4a4a4";
	const getStatusMessage = (status: string) => STATUS_MESSAGES[status.toLowerCase()] || status;

	const renderToolTip = (urlInfo: TUrlImportItem) => {
		const color = getStatusColor(urlInfo.status);
		const tooltipStatuses = ["bad url", "not found", "import error", "scraping error"];

		if (!color || !tooltipStatuses.includes(urlInfo.status.toLowerCase())) {
			return null;
		}

		return (
			<BulkImportReportToolTip
				color={color}
				status={urlInfo.status}
				reason={urlInfo?.reason}
				error_reason={urlInfo?.error_reason}
				url={urlInfo.url}
			/>
		);
	};

	// Render a message showing how many unique and valid LinkedIn URLs were found
	const renderUniqueUrlMessage = () =>
		linkedInUrl && (
			<>
				Out of <strong style={{color: "#2C3030"}}>{totalURLs}</strong>{" "}
				{labels.entityNamePlural},{" "}
				<strong style={{color: "#2C3030"}}>
					{linkedInUrl.length - (counts.badURL + counts.notFound)}
				</strong>{" "}
				unique and valid URLs found.
				<br />
				<br />
			</>
		);

	return (
		<div className={styles.reportContainer}>
			<h3>{labels.modalTitle}</h3>
			<hr />
			{linkedInUrl &&
			linkedInUrl.length > 0 &&
			counts.finished === linkedInUrl.length &&
			counts.inProgress === 0 ? (
				// All imports are fully completed (no in-progress items)
				<section style={{marginBottom: "20px"}}>
					<div className={styles.header} data-testid={"BulkImportReport"}>
						{counts.error === 0 ? (
							<>
								<ExclamatoryIcon />
								<p style={{color: "#0C5850FF"}}>
									Imports {lastCompletedDisplay ? "were" : "are"} completed
								</p>
							</>
						) : (
							<>
								<BiErrorCircle size={"20px"} color={"#F5A300"} />
								<p style={{color: "#F5A300", fontWeight: "500"}}>
									Imports {lastCompletedDisplay ? "were" : "are"} completed with
									errors
								</p>
							</>
						)}
					</div>
					<p className={counts.error === 0 ? styles.successCount : styles.errorCount}>
						{totalURLs > linkedInUrl.length - counts.badURL - counts.notFound &&
							renderUniqueUrlMessage()}
						<strong>{counts.success}</strong> {labels.entityNamePlural} successfully{" "}
						{source === "project" ? (
							<>
								added to your <strong>{project?.name}</strong> project
							</>
						) : (
							"imported"
						)}
					</p>
				</section>
			) : counts.inProgress > 0 && counts.finished > 0 ? (
				// Partial completion: some profiles are done, some still processing
				<section style={{marginBottom: "20px"}}>
					<div className={styles.header}>
						<BiRefresh size={20} className={styles.refresh} />
						<p style={{color: "#444444", fontWeight: "500"}}>Import in progress</p>
					</div>
					<p className={styles.successCount}>
						<strong>{counts.success}</strong> {labels.entityNamePlural} successfully{" "}
						{source === "project" ? (
							<>
								added to your <strong>{project?.name}</strong> project
							</>
						) : (
							"imported"
						)}
						<br />
						<strong>{counts.inProgress}</strong> {labels.entityNamePlural} still being
						imported
						{counts.error > 0 && (
							<>
								<br />
								<span style={{color: "#F5A300"}}>
									<strong>{counts.error}</strong> {labels.entityNamePlural} had
									errors
								</span>
							</>
						)}
					</p>
				</section>
			) : (
				// All items are still in progress - show progress bar
				inProgress && (
					<BulkImportReportProgressBar
						percentage={importPercentage}
						progressCount={progressCountDisplayString}
					/>
				)
			)}
			{/* Display a live list of all imported/importing LinkedIn URLs and their statuses */}
			<div className={styles.reportBox}>
				{linkedInUrl &&
					linkedInUrl.map((urlInfo) => (
						<div className={styles.reportItems} key={urlInfo.url}>
							<p className={styles.urlColumn}>{`/${urlInfo.url.split("/").pop()}`}</p>
							<div
								className={styles.statuses}
								style={{color: getStatusColor(urlInfo.status)}}
							>
								{renderToolTip(urlInfo)}
								<span className={styles.status}>
									{getStatusMessage(urlInfo.status)}
								</span>
							</div>
							<span className={styles.etaColumn}>
								{isInProgressStatus(urlInfo.status)
									? formatETA(urlInfo.eta_expected_at, urlInfo.eta_original)
									: "-"}
							</span>
						</div>
					))}
			</div>

			{/* Show download report buttons if the import process is completed */}
			{isEffectivelyComplete && !isLastBulkImportDataLoading && (
				<div className={styles.downloadReport} data-testid={"DownloadReportButton"}>
					{inProgress && (
						<a onClick={() => handleDownload(linkedInUrl)}>
							<DownloadIcon /> <span>Download CSV report</span>
						</a>
					)}
					{importedLinkedInUrls && importedLinkedInUrls?.length > 0 && (
						<a
							onClick={() => !loadingImportHistoryDownload && handleDownload(null)}
							className={classNames(styles.downloadAllReport, {
								[styles.disabled]: loadingImportHistoryDownload,
							})}
						>
							{loadingImportHistoryDownload ? (
								<Loader type={"TailSpin"} height={"13px"} width={"12px"} />
							) : (
								<DownloadIcon />
							)}
							<span>Download all imports in CSV</span>
						</a>
					)}
				</div>
			)}

			{/* Final button to complete the import or add more */}
			<Button
				name={"Done"}
				type={"button"}
				data-testid={"DoneButton"}
				className={classNames(styles.addButton)}
				onClick={() => handleClick(project?.id ?? 0)}
				disabled={!isEffectivelyComplete || isLastBulkImportDataLoading}
			>
				{!isEffectivelyComplete && <BiRefresh size={22} className={styles.refresh} />}{" "}
				{isEffectivelyComplete
					? `Add ${
							linkedInUrl && linkedInUrl.length > 0 ? "more" : ""
					  } ${labels.entityNamePlural.toLowerCase()}`
					: labels.importingLabel}
			</Button>
		</div>
	);
};

export default BulkImportReport;
